import type React from "react";
import { trackOrderStatus, orderStatusEnum } from "../../lib/constants";
import type {
  Order,
  OrderHistoryType,
  TransferHistoryEntry,
} from "../../lib/types";
import { formatDate } from "../../lib/utils";
import { Button } from "../ui/button";
import { RefreshCcw } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { useAuth } from "../../lib/authContext";

export const OrderHistory: React.FC<{
  order: Order;
  onClose: () => void;
}> = ({ order, onClose }) => {
  const { toast } = useToast();
  const { accessLevel } = useAuth();
  // Sort history by id in ascending order
  const sortedHistory = [...order.order_history].sort(
    (a: OrderHistoryType, b: OrderHistoryType) => a.id - b.id,
  );

  // --- Transfer History Integration ---
  // Sort transfer history by created_at ascending, then id as tiebreaker
  const transferHistory: TransferHistoryEntry[] = (order.transfer_history ?? [])
    .slice()
    .sort((a: TransferHistoryEntry, b: TransferHistoryEntry) => {
      return (a.id || 0) - (b.id || 0);
    });

  // Check if any special statuses exist in the order history
  const hasRejectedStatus = sortedHistory.some(
    (history: OrderHistoryType) =>
      history.status === orderStatusEnum.Rejected ||
      history.status === orderStatusEnum.Cancelled ||
      history.status === orderStatusEnum.RTO,
  );

  // If rejected status exists, just show the sorted history
  // Otherwise, show completed statuses + pending statuses from trackOrderStatus
  const displayStatuses = hasRejectedStatus
    ? sortedHistory.map(history => history.status)
    : [
        // First show all completed statuses in the order they occurred
        ...sortedHistory.map(history => history.status),
        // Then show pending statuses that aren't already completed
        ...trackOrderStatus.filter(
          status =>
            status !== orderStatusEnum.Rejected && // Don't show Rejected in non-rejected orders
            !sortedHistory.some(history => history.status === status),
        ),
      ];

  const retriggerOrderStatus = async (order_id: number, status: string) => {
    const response = await api.retriggerOrderStatus(order_id, status);
    onClose();
    toast({
      title: "Order status re-triggered to Medibuddy",
      description: `${status} status for ${order.invoice_number} re-triggered`,
    });
  };

  return (
    <div className="relative p-3">
      <div className="space-y-4">
        {/* Transfer History Timeline */}
        {transferHistory.length > 1 && (
          <div className="mb-4">
            <div className="text-gray-500 dark:text-gray-400 flex justify-between">
              <div className="font-bold">Transfer Chain</div>
              <div className="font-bold">Timestamp</div>
            </div>
            {transferHistory.map(
              (transfer: TransferHistoryEntry, idx: number) => {
                const prev = idx > 0 ? transferHistory[idx - 1] : null;
                const isFirst = idx === 0;
                const isLast = idx === transferHistory.length - 1;
                // For last entry, check if this order is the source (original creator)
                const isSourceOrder =
                  Number(order.entity_id) ===
                  Number(transferHistory[0]?.entity_id);
                return (
                  <div
                    key={transfer.order_id}
                    className="flex items-center justify-between py-1"
                  >
                    <div>
                      {isFirst ? (
                        <span className="text-gray-700 dark:text-gray-200">
                          Created at
                        </span>
                      ) : isLast ? (
                        isSourceOrder ? (
                          <span className="text-blue-600 dark:text-blue-400">
                            Transferred to{" "}
                            <b>{transfer.entity_name || transfer.entity_id}</b>
                          </span>
                        ) : (
                          <span className="text-blue-600 dark:text-blue-400">
                            Transferred from{" "}
                            <b>{prev?.entity_name || prev?.entity_id}</b>
                          </span>
                        )
                      ) : (
                        <span className="text-blue-600 dark:text-blue-400">
                          Transferred from{" "}
                          <b>{prev?.entity_name || prev?.entity_id}</b> to{" "}
                          <b>{transfer.entity_name || transfer.entity_id}</b>
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400 text-sm">
                      {transfer.created_at
                        ? formatDate(new Date(transfer.created_at))
                        : "-"}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        )}
        {/* Status History Timeline */}
        <div className="text-gray-500 dark:text-gray-400 flex justify-between">
          <div className="font-bold">Status</div>
          <div className="font-bold">Timestamp</div>
          <div className="font-bold">ETA</div>
          {(!accessLevel?.access_level?.provider_id ||
            accessLevel.access_level.provider_id.length === 0) && (
            <div className="font-bold">Action (To Medibuddy)</div>
          )}
        </div>
        {displayStatuses.map((status, index) => {
          const historyItem = sortedHistory.find(
            (history: OrderHistoryType) => history.status === status,
          );
          const isCompleted = !!historyItem;
          const isLastItem = index === displayStatuses.length - 1;
          const isOnhold =
            status === orderStatusEnum.OnHold ||
            status === orderStatusEnum.Cancelled ||
            status === orderStatusEnum.RTO;

          return (
            <div
              key={`${status}-${historyItem?.id}`}
              className="relative flex items-start"
            >
              <div className="absolute flex items-center justify-center">
                <div
                  className={`w-3 h-3 rounded-full border-2 ${
                    isCompleted
                      ? "bg-gray-500 border-gray-500"
                      : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500"
                  }`}
                />
              </div>
              <div className="ml-10 flex-grow">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <h4
                      className={`font-medium ${
                        isOnhold
                          ? "bg-red-500 text-white dark:text-yellow-400 px-2 py-1 rounded-md"
                          : "text-gray-900 dark:text-gray-100"
                      }`}
                    >
                      {status}
                    </h4>
                  </div>
                  <span className="text-gray-500 dark:text-gray-400">
                    {historyItem
                      ? formatDate(new Date(historyItem.created_at))
                      : "Pending"}
                    {historyItem?.user_email && (
                      <div className="text-gray-400 dark:text-gary-400">
                        by {historyItem?.user_email}
                      </div>
                    )}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400">
                    {historyItem?.api_body?.eta
                      ? new Date(Number(historyItem.api_body.eta)).toLocaleString(
                          "en-IN",
                          {
                            timeZone: "Asia/Kolkata",
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )
                      : "–"}
                  </span>
                  {(!accessLevel?.access_level?.provider_id ||
                    accessLevel.access_level.provider_id.length === 0) && (
                    <div className="text-gray-500 dark:text-gray-400">
                      <Button
                        variant="outline"
                        disabled={!historyItem}
                        className={`${
                          !historyItem ? "cursor-not-allowed" : ""
                        }`}
                        onClick={() =>
                          retriggerOrderStatus(Number(order.id), status)
                        }
                      >
                        Re-trigger
                        <RefreshCcw className="ml-2 w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {!isLastItem && (
                <div
                  className={`absolute left-1 top-3.5 h-full w-0.5 ${
                    isCompleted
                      ? "bg-gray-500"
                      : "border-dotted bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-500"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
